import { Link } from 'react-router-dom';
import {
  ArrowRight, Cpu, Layers, Zap, Globe, Brush, Wand2, Video, Film, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';
import SplitText from '../cinematic/SplitText';
import MagneticButton from '../cinematic/MagneticButton';

export default function About() {
  return (
    <div className="min-h-screen text-white cinematic-grain relative" data-testid="about-page">
      <CinematicBackdrop intensity="normal" />
      <Navbar />

      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-20 pb-24 relative z-10">
        {/* Hero */}
        <div className="relative text-center mb-16">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A3FF00] mb-4">About DIS2ORT</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[88px] font-black tracking-[-0.02em] uppercase leading-[0.95] mb-6" data-testid="about-heading">
            <SplitText text="ONE STUDIO." className="block" stagger={0.04} duration={0.7} />
            <span className="italic bg-gradient-to-r from-[#FF6B4A] via-[#E040A0] to-[#FFD93D] bg-clip-text text-transparent">
              <SplitText text="EVERY AI MODEL." stagger={0.05} duration={0.8} delay={0.4} />
            </span>
          </h1>
          <p className="text-base lg:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            DIS2ORT is the only AI image &amp; video studio that gives you DALL·E 3, Midjourney v7, FLUX, Sora 2, Kling, Runway, and 19 other models in a single tab.
            No more juggling 6 subscriptions. No more learning 6 UIs. One login. One bill.
          </p>
        </div>

        {/* Mission */}
        <div className="relative grid md:grid-cols-3 gap-3 mb-16" data-testid="about-mission">
          {[
            { icon: Layers, title: 'Every model, one tab', body: 'Stop switching between Midjourney, Runway, Pika, Leonardo. We integrate them all so you only need one workflow.', color: '#FF6B4A' },
            { icon: Zap,    title: 'Instant cloud rendering', body: 'Server-side models return in 5–15 seconds. No GPU setup, no Discord bots, no waiting rooms.', color: '#FFD93D' },
            { icon: Globe,  title: 'Public showcase, private library', body: 'Build a portfolio with the community, keep work-in-progress private. Public files are crawlable for SEO.', color: '#E040A0' },
          ].map((c) => (
            <div key={c.title} className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${c.color}22`, border: `1px solid ${c.color}44` }}>
                <c.icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <h3 className="font-display text-xl font-bold tracking-tight mb-2">{c.title}</h3>
              <p className="text-xs text-white/60 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        {/* What you can do */}
        <div className="relative mb-16">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF6B4A] text-center mb-3">What you can do</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight uppercase text-center mb-10">A creative toolbox, not a toy.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Sparkles, label: 'Text → Image', desc: '150 models, 42 styles' },
              { icon: Video,    label: 'Text → Video', desc: 'Sora 2, Kling, Runway' },
              { icon: Film,     label: 'Image → Video', desc: 'Animate any still' },
              { icon: Brush,    label: 'Sketch + Inpaint', desc: 'Draw & repaint regions' },
              { icon: Wand2,    label: 'Prompt Enhance', desc: 'AI rewrites your idea' },
              { icon: Cpu,      label: 'Real-ESRGAN Upscale', desc: '2× / 4× quality boost' },
              { icon: Layers,   label: '42 Style Presets', desc: 'Anime, Ghibli, Cinematic…' },
              { icon: Globe,    label: 'Community Feed', desc: 'Share & remix publicly' },
            ].map((c) => (
              <div key={c.label} className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                <c.icon className="w-4 h-4 text-[#FF6B4A] mb-2" />
                <div className="text-xs font-bold uppercase tracking-wider">{c.label}</div>
                <div className="text-[10px] text-white/50 mt-1">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="relative p-8 lg:p-12 rounded-3xl bg-gradient-to-br from-[#FF6B4A]/15 via-[#E040A0]/10 to-[#FFD93D]/15 border border-white/[0.08] text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4">
            50 free credits.<br />
            <span className="italic bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] bg-clip-text text-transparent">No card required.</span>
          </h3>
          <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">Sign in with Google. Get 50 credits. Try every model.</p>
          <Link to="/login">
            <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] px-10 py-6 text-sm rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_12px_40px_rgba(255,107,74,0.4)] transition-shadow" data-testid="about-cta">
              Start Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
